import { afterRenderEffect, Component, DestroyRef, inject, OnInit, signal, viewChild } from '@angular/core'
import { takeUntilDestroyed } from '@angular/core/rxjs-interop'
import { FormGroup } from '@angular/forms'
import { ActivatedRoute, NavigationEnd, Router } from '@angular/router'
import { AuthService } from '@app/modules/auth/services/auth.service'
import { WorkspaceRoleService } from '@app/modules/auth/services/workspace-role.service'
import { BillingService } from '@app/modules/payments/services/billing.service'
import { Workspace } from '@app/modules/workspaces/interfaces/workspace.interface'
import { PaymentType } from '@app/modules/workspaces/models/payment-type'
import { ORGANIZATION_SETTINGS_WORKSPACE_QUERY_PARAM } from '@app/v2/settings/helpers/organization-settings-url.helper'
import { OrganizationService } from '@app/v2/settings/services/organization.service'
import { MicetribeAdmin, OrganizationSettings, WorkspacePaymentOption } from '@app/v2/settings/types/organization-settings.type'
import { Alert } from '@app/v2/shared/enums/alert.enum'
import { AlertService } from '@app/v2/shared/services/alert.service'
import { AppService } from '@app/v2/shared/services/app.service'
import { IndustrySector } from '@app/v2/shared/types/industry-sector.type'
import { InviteTeamMemberPayload, OrganizationUser, USER_ROLES } from '@app/v2/shared/types/user.type'
import { TranslateModule, TranslateService } from '@ngx-translate/core'
import { filter, finalize } from 'rxjs'
import { ApiKeysComponent } from '../../api-keys/api-keys.component'
import { BillingComponent } from '../../billing-payments/billing/billing.component'
import { LegalInfoComponent } from '../../legal-info/legal-info.component'
import { TeamMemberActionPayload } from '../../manage-team/constants/team-member-action'
import { ManageTeamComponent } from '../../manage-team/manage-team.component'
import { MicetribeAdminComponent } from '../../micetribe-admin/micetribe-admin.component'
import { PublicInfoComponent } from '../../public-info/public-info.component'
import { WebhooksComponent } from '../../webhooks/webhooks.component'

@Component({
  selector: 'app-organization-settings-content',
  imports: [LegalInfoComponent, TranslateModule, PublicInfoComponent, MicetribeAdminComponent, WebhooksComponent, BillingComponent, ApiKeysComponent, ManageTeamComponent],
  templateUrl: './organization-settings-content.component.html',
  styleUrl: './organization-settings-content.component.scss',
  standalone: true
})
export class OrganizationSettingsContentComponent implements OnInit {
  public legalInfo = viewChild(LegalInfoComponent)
  public publicInfo = viewChild(PublicInfoComponent)
  public micetribeAdmin = viewChild(MicetribeAdminComponent)
  public billing = viewChild(BillingComponent)
  public manageTeam = viewChild(ManageTeamComponent)
  public industrySectors = signal<IndustrySector[]>([])
  public organizationData = signal<OrganizationSettings | null>(null)
  public micetribeAdminData = signal<MicetribeAdmin | null>(null)
  public availableGateways = signal<PaymentType[]>([])
  public isLoading = signal<boolean>(true)
  public isAdmin = signal<boolean>(false)
  public canAccessWebhooks = signal<boolean>(false)
  public canAccessApiKeys = signal<boolean>(false)
  public canAccessManageTeam = signal<boolean>(false)
  public organizationUsers = signal<OrganizationUser[]>([])
  public isLoadingUsers = signal<boolean>(false)
  public isLoadingMoreUsers = signal<boolean>(false)
  public hasMoreUsers = signal<boolean>(true)
  public isInvitingTeamMember = signal<boolean>(false)
  public isUpdatingTeamMember = signal<boolean>(false)

  public isAuthorized = signal<boolean>(false)
  public canAccessBilling = signal<boolean>(false)
  /** Accountants only see the billing section in organization settings. */
  public isAccountantBillingOnly = signal<boolean>(false)
  private _currentWorkspaceId!: number | string
  private _usersPage = 1
  private readonly _usersPerPage = 10
  private _destroyRef = inject(DestroyRef)
  private _lastScrolledFragment: string | null = null
  private readonly _urlFragment = signal<string | null>(null)

  constructor(
    private _appService: AppService,
    private _organizationService: OrganizationService,
    private _router: Router,
    private _alertService: AlertService,
    private _translateService: TranslateService,
    private _authService: AuthService,
    private _billingService: BillingService,
    private _workspaceRoleService: WorkspaceRoleService,
    private _activatedRoute: ActivatedRoute
  ) {
    afterRenderEffect(() => this._scrollToFragmentIfReady())
  }

  ngOnInit(): void {
    this._urlFragment.set(this._parseUrlFragment(this._router.url))
    this._router.events
      .pipe(
        filter((event): event is NavigationEnd => event instanceof NavigationEnd),
        takeUntilDestroyed(this._destroyRef)
      )
      .subscribe((event) => {
        this._urlFragment.set(this._parseUrlFragment(event.urlAfterRedirects))
      })

    this._currentWorkspaceId = this._authService.currentWorkspace?.id ?? this._activatedRoute.snapshot.queryParamMap.get(ORGANIZATION_SETTINGS_WORKSPACE_QUERY_PARAM)
    // Current workspace first; query param covers new-tab / SSO deep links until session is hydrated.
    if (!this._currentWorkspaceId) {
      this._router.navigateByUrl('/workspaces/list')
      return
    }

    this.industrySectors.set(this._appService.industrySectors)
    this._loadOrganizationData()
    this._loadMicetribeAdminData()
    this._loadAvailablePaymentGateways()
    this.isAuthorized.set(this._authService.canEditOrganization || this._workspaceRoleService.canAccessBilling())
    this._updateRoleAccess()
    this.isAdmin.set(this._authService.role === 'Admin')
    this.canAccessWebhooks.set(this._workspaceRoleService.canAccessWebhooks())
    this._syncManageTeamAccess()

    this._workspaceRoleService
      .getUserRole()
      .pipe(takeUntilDestroyed(this._destroyRef))
      .subscribe(() => {
        this._updateRoleAccess()
        this._syncManageTeamAccess()
      })
  }

  /**
   * Sync section visibility with the current workspace role once it has loaded.
   */
  private _updateRoleAccess(): void {
    this.isAdmin.set(this._authService.role === 'Admin' || this._workspaceRoleService.isSuperAdmin())
    this.canAccessWebhooks.set(this._workspaceRoleService.canAccessWebhooks())
    this.canAccessBilling.set(this._workspaceRoleService.canAccessBilling())
    this.canAccessApiKeys.set(this._workspaceRoleService.canAccessApiKeys())
    // Superadmins keep the full org-settings experience even if a workspace role look-up lags.
    this.isAccountantBillingOnly.set(this._workspaceRoleService.isAccountant() && !this._workspaceRoleService.isSuperAdmin())
  }

  /**
   * Parse the hash fragment from a router URL.
   */
  private _parseUrlFragment(url: string): string | null {
    return this._router.parseUrl(url).fragment
  }

  /**
   * Scroll once the target section exists in the DOM and its content is ready to display.
   */
  private _scrollToFragmentIfReady(): void {
    const fragment = this._urlFragment()
    if (!fragment || this._lastScrolledFragment === fragment || !this._isSectionReady(fragment)) {
      return
    }

    const element = document.getElementById(fragment)
    if (!element) {
      return
    }

    this._lastScrolledFragment = fragment
    element.scrollIntoView({ behavior: 'smooth', block: 'center' })
  }

  /**
   * Check whether a fragment's section is visible and finished its initial render.
   */
  private _isSectionReady(fragment: string): boolean {
    if (this.isAccountantBillingOnly() && fragment !== 'billing') {
      return false
    }

    switch (fragment) {
      case 'public-info':
      case 'legal-info':
        return !this.isAccountantBillingOnly()
      case 'billing':
        if (!this.canAccessBilling()) {
          return false
        }

        const billing = this.billing()
        return !!billing && !billing.isLoading()
      case 'micetribe-admin':
        return this.isAdmin() && !this.isAccountantBillingOnly()
      case 'webhooks':
        return this.canAccessWebhooks()
      case 'api-keys':
        return this.canAccessApiKeys()
      default:
        return true
    }
  }

  private _loadOrganizationData(): void {
    this._organizationService
      .getOrganizationSettings(this._currentWorkspaceId)
      .pipe(takeUntilDestroyed(this._destroyRef))
      .subscribe((res: OrganizationSettings) => {
        this.organizationData.set(res)
        if (!!this.organizationData() && this.availableGateways().length) this.isLoading.set(false)
      })
  }

  /**
   * @description Loads the micetribe admin data
   */
  private _loadMicetribeAdminData(): void {
    const currentWorkspace = this._authService.currentWorkspace
    if (!currentWorkspace) return

    const micetribeAdminData: MicetribeAdmin = {
      plan_id: currentWorkspace.plan_id,
      pro: currentWorkspace.plan_id === 2,
      plan_expiry_date: currentWorkspace.plan_expiry_date || '',
      payments:
        currentWorkspace.options?.payments && currentWorkspace.options.payments !== null && !Array.isArray(currentWorkspace.options.payments)
          ? [currentWorkspace.options.payments as WorkspacePaymentOption]
          : undefined,
      parent: currentWorkspace.parent_workspace?.id || null
    }
    this.micetribeAdminData.set(micetribeAdminData)
  }

  /**
   * @description Loads the available payment gateways
   */
  private _loadAvailablePaymentGateways(): void {
    this._billingService
      .getAvalaibalePaymentGetways()
      .pipe(takeUntilDestroyed(this._destroyRef))
      .subscribe((gateways: PaymentType[]) => {
        // Sort gateways alphabetically by label (display name)
        const sortedGateways: PaymentType[] = gateways.sort((a: PaymentType, b: PaymentType) => (a.label > b.label ? 1 : -1))
        this.availableGateways.set(sortedGateways)
        this.isLoading.set(false)
      })
  }

  private _syncManageTeamAccess(): void {
    this.canAccessManageTeam.set(this._workspaceRoleService.canAccessManageTeam())
  }

  /**
   * @description Loads the first page of organization users
   */
  public loadOrganizationUsers(): void {
    if (!this.canAccessManageTeam()) return
    this._loadOrganizationUsers({ page: 1, reset: true })
  }

  /**
   * @description Loads the next page of organization users
   */
  public loadMoreOrganizationUsers(): void {
    if (!this.canAccessManageTeam() || this.isLoadingUsers() || this.isLoadingMoreUsers() || !this.hasMoreUsers()) return
    this._loadOrganizationUsers({ page: this._usersPage + 1, reset: false })
  }

  private _resolveHasMoreUsers(response: { current_page?: number; last_page?: number; total?: number; per_page?: number }, page: number, receivedCount: number): boolean {
    const perPage = response.per_page ?? this._usersPerPage

    if (typeof response.total === 'number') {
      return page * perPage < response.total
    }

    if (typeof response.current_page === 'number' && typeof response.last_page === 'number') {
      return response.current_page < response.last_page
    }

    return receivedCount >= perPage
  }

  /**
   * @description Loads the organization users
   * @param {number} page Page number
   * @param {boolean} reset Reset the users
   */
  private _loadOrganizationUsers(options: { page: number; reset: boolean }): void {
    if (!this.canAccessManageTeam()) return

    if (options.reset) {
      this._usersPage = 1
      this.organizationUsers.set([])
      this.isLoadingUsers.set(true)
      this.hasMoreUsers.set(true)
    } else {
      this.isLoadingMoreUsers.set(true)
    }

    this._organizationService
      .getOrganizationUsers(this._currentWorkspaceId, options.page, this._usersPerPage)
      .pipe(
        takeUntilDestroyed(this._destroyRef),
        finalize(() => {
          this.isLoadingUsers.set(false)
          this.isLoadingMoreUsers.set(false)
        })
      )
      .subscribe({
        next: (response) => {
          const users = response.data ?? []
          this._usersPage = options.page
          this.organizationUsers.set(options.reset ? users : [...this.organizationUsers(), ...users])
          const hasMore = this._resolveHasMoreUsers(response, options.page, users.length)
          this.hasMoreUsers.set(hasMore)
        },
        error: (error) => {
          this.hasMoreUsers.set(false)

          if (options.reset) {
            this.organizationUsers.set([])
          }

          const message = error.error?.message || 'Failed to load team members.'
          this._alertService.alert(Alert.ERROR, message)
        }
      })
  }

  /**
   * Invites a team member from manage-team modal payload.
   * @param {InviteTeamMemberPayload} payload Invite team member payload
   * @returns {void}
   */
  public inviteTeamMember(payload: InviteTeamMemberPayload): void {
    if (this.isInvitingTeamMember()) return

    this.isInvitingTeamMember.set(true)
    this._organizationService
      .inviteTeamMember(+this._currentWorkspaceId, payload)
      .pipe(
        takeUntilDestroyed(this._destroyRef),
        finalize(() => this.isInvitingTeamMember.set(false))
      )
      .subscribe({
        next: (response) => {
          this._alertService.alert(Alert.SUCCESS, response?.message)
          this.manageTeam()?.closeInviteTeamMembersModal()
          this.loadOrganizationUsers()
        },
        error: (error) => {
          this._alertService.alert(Alert.ERROR, error?.message)
        }
      })
  }

  /**
   * Suspends, removes, or activates a team member based on the selected action.
   * @param payload User invite ID, API action, and optional notify flag
   */
  public updateTeamMember(payload: TeamMemberActionPayload): void {
    if (this.isUpdatingTeamMember()) return

    this.isUpdatingTeamMember.set(true)
    this._organizationService
      .performTeamMemberAction(+this._currentWorkspaceId, payload.id, payload.action, payload.notify)
      .pipe(
        takeUntilDestroyed(this._destroyRef),
        finalize(() => this.isUpdatingTeamMember.set(false))
      )
      .subscribe({
        next: (response) => {
          this._alertService.alert(Alert.SUCCESS, response?.message)
          this.loadOrganizationUsers()
        },
        error: (error) => {
          this._alertService.alert(Alert.ERROR, error?.error?.message)
        }
      })
  }

  /**
   * Updates the role of a team member
   * @param {number} id Team member invite ID
   * @param {USER_ROLES} role Team member role
   * @returns {void}
   */
  public updateTeamMemberRole(payload: { id: number; role: USER_ROLES }): void {
    if (this.isUpdatingTeamMember()) return

    this.isUpdatingTeamMember.set(true)
    this._organizationService
      .updateTeamMemberRole(+this._currentWorkspaceId, payload.id, payload.role)
      .pipe(
        takeUntilDestroyed(this._destroyRef),
        finalize(() => this.isUpdatingTeamMember.set(false))
      )
      .subscribe({
        next: (response) => {
          this._alertService.alert(Alert.SUCCESS, response?.message)
          this.loadOrganizationUsers()
        },
        error: (error) => {
          this._alertService.alert(Alert.ERROR, error?.error?.message)
        }
      })
  }

  /**
   * @description Updates organization settings
   * @returns {void}
   */
  public updateOrganizationSettings(): void {
    const legalForm = this.legalInfo()?.form()
    const publicForm = this.publicInfo()?.publicInfoFormGroup()
    const forms = [legalForm, publicForm].filter(Boolean) as FormGroup[]

    if (forms.length === 0) return

    // Check if any form has changes and build payload
    let hasDirtyChanges: boolean = false
    let mergedPayload: Partial<OrganizationSettings & { logo?: string }> = {}

    for (const form of forms) {
      const isDirty = this._organizationService.isFormDirty(form)
      const isValid = this._organizationService.isFormValid(form)
      const payload = this._organizationService.getUpdatePayload(form)

      if (isDirty || isValid) hasDirtyChanges = true
      if (Object.keys(payload).length > 0) {
        mergedPayload = { ...mergedPayload, ...payload }
      }
    }

    if (!hasDirtyChanges || Object.keys(mergedPayload).length === 0) return

    const requestPayload = {
      workspace_id: +this._currentWorkspaceId,
      ...mergedPayload
    }

    const handleFormsAfterResponse = (action: 'success' | 'validation-error' | 'clear', errorData?: any) => {
      for (const form of forms) {
        if (action === 'success') {
          this._organizationService.handleServerValidationErrors(form) // Clear only
          this._organizationService.markFormPristine(form)
        } else if (action === 'validation-error') {
          this._organizationService.handleServerValidationErrors(form, errorData, true) // Clear + apply with persistence
        } else {
          this._organizationService.handleServerValidationErrors(form) // Clear only
        }
      }
    }

    this._organizationService
      .updateOrganizationSettings(requestPayload)
      .pipe(takeUntilDestroyed(this._destroyRef))
      .subscribe({
        next: (updated) => {
          this.organizationData.set(updated)
          handleFormsAfterResponse('success')

          // Update localStorage if workspace name was changed
          if ((mergedPayload.workspace_name || mergedPayload.logo) && this._authService.currentWorkspace) {
            this._authService.currentWorkspace = {
              ...this._authService.currentWorkspace,
              name: mergedPayload.workspace_name,
              logo: this._authService.currentWorkspace.logo
            }
          }

          this._alertService.alert(Alert.SUCCESS, this._translateService.instant('v2.orgSettings.updateSuccess'))
        },
        error: (error) => {
          if (error.status === 403) {
            this._alertService.alert(Alert.ERROR, this._translateService.instant('v2.orgSettings.updateError'))
          } else if (error.status === 422) handleFormsAfterResponse('validation-error', error.error)
          else {
            handleFormsAfterResponse('clear')
            this._alertService.alert(Alert.ERROR, error.error.message)
          }
        }
      })
  }

  /**
   * @description Updates Micetribe admin settings (plan, payment gateway, expiry date)
   * @returns {void}
   */
  public updateMicetribeAdminSettings(): void {
    const micetribeForm = this.micetribeAdmin()?.proForm()

    if (!micetribeForm || !this._organizationService.isFormDirty(micetribeForm)) return

    // Build the workspace update payload - only include changed fields
    const workspacePayload: Partial<Workspace> = {
      id: +this._currentWorkspaceId
    }

    // Process dirty controls with workspace-specific mappings
    const controlMappings = {
      pro: (value: boolean) => ({ plan_id: value ? 2 : 1 }),
      planExpiryDate: (value: string) => ({
        plan_expiry_date: value && value.trim() !== '' ? value + 'T00:00:00.000000Z' : null
      }),
      paymentGateway: (value: string) => ({
        options: {
          ...this._authService.currentWorkspace?.options,
          payments: value && value.trim() !== '' ? { gateway: value } : []
        }
      })
    }

    Object.keys(micetribeForm.controls).forEach((controlName) => {
      const control = micetribeForm.controls[controlName]
      if (control.dirty && controlMappings[controlName]) Object.assign(workspacePayload, controlMappings[controlName](control.value))
    })

    this._organizationService
      .updateMicetribeAdminSettings(workspacePayload as Workspace)
      .pipe(takeUntilDestroyed(this._destroyRef))
      .subscribe({
        next: (updatedWorkspace) => {
          // Update the current workspace in localStorage
          if (this._authService.currentWorkspace) {
            const currentWorkspace = this._authService.currentWorkspace

            // Merge the updated data, ensuring null values are preserved
            const mergedWorkspace = {
              ...currentWorkspace,
              ...updatedWorkspace
            }

            // If plan_expiry_date was set to null, ensure it stays null
            if (updatedWorkspace.plan_expiry_date === null) mergedWorkspace.plan_expiry_date = null

            // If payments was set to empty array, ensure it stays empty array
            if (updatedWorkspace.options?.payments && Array.isArray(updatedWorkspace.options.payments) && updatedWorkspace.options.payments.length === 0) {
              mergedWorkspace.options = {
                ...mergedWorkspace.options,
                payments: []
              }
            }
            this._authService.currentWorkspace = mergedWorkspace
            this._loadMicetribeAdminData()
          }

          // Mark form as pristine after successful update
          if (micetribeForm) this._organizationService.markFormPristine(micetribeForm)

          this._alertService.alert(Alert.SUCCESS, this._translateService.instant('v2.orgSettings.updateSuccess'))
        },
        error: (error) => {
          if (error.status === 403) this._alertService.alert(Alert.ERROR, this._translateService.instant('v2.orgSettings.updateError'))
          else if (error.status === 422 && error.error) this._organizationService.handleServerValidationErrors(micetribeForm, error.error, true)
          else this._alertService.alert(Alert.ERROR, error.error?.message || 'An error occurred')
        }
      })
  }
}
